'use client';

import { useContext, useState } from 'react';

import { ApplicationConfigContext } from '@/components/common/ApplicationConfigProvider/ApplicationConfigProvider';
import { Arrow } from '@/components/common/Icons/Arrow';
import { BackArrow } from '@/components/common/Icons/BackArrow';
import { CartContext } from '@/app/(ordering)/shop/CartProvider/CartProvider';
import type { DeliveryCurrentView } from './DeliveryPanel';
import GooglePlacesAutocomplete from 'react-google-places-autocomplete';
import { Loading } from '@/components/common/Icons/Loading';
import { StartOrderAddressCard } from '@/components/ordering/StartOrderAddressCard/StartOrderAddressCard';
import type { deliveryAddressProps } from '../../data/shopdata.type';
import { renderToString } from 'react-dom/server';
import styles from '../../../shop.module.css';

const searchIconSVGString = (
	<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16">
		<path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z"></path>
	</svg>
);

const searchIcon = (isLoading: boolean) => ({
	background: `url("data:image/svg+xml,${encodeURI(
		renderToString(isLoading ? <Loading size="small" fill="green" /> : searchIconSVGString)
	)}") no-repeat 10px center;`, // Adjust padding for the icon
	paddingLeft: '10px',
	fontSize: '18px',
	alignItems: 'center',
	display: 'flex',
	height: '100%',
});

type DeliverySearchProps = {
	setCurrentView: (view: DeliveryCurrentView) => void;
	isAddressAvailable: boolean;
	setSelectedAddress: (newAddress: MinimalGeocodeResult[] | undefined) => void;
	deliveryAddress: deliveryAddressProps | undefined;
	setDeliveryAddress: (newAddress: deliveryAddressProps | undefined) => void;
};

export type fullAddressProps = {
	administrative_area_level_1: string;
	administrative_area_level_2: string;
	country: string;
	locality: string;
	sublocality: string;
	neighborhood: string;
	postal_code: string;
	postal_code_suffix: string;
	route: string;
	street_number: string;
};

// Suggestion from autocomplete
export type PlaceSuggestion = {
	place_id: string;
	description: string;
};

// Only the parts of GeocoderResult we actually use
export type MinimalGeocodeResult = {
	geometry: google.maps.GeocoderGeometry;
	address_components: google.maps.GeocoderAddressComponent[];
	types: string[];
};

export const extractAddressFromComponents = (
	addressComponents: google.maps.GeocoderAddressComponent[]
): Partial<fullAddressProps> => {
	const result: Partial<fullAddressProps> = {};

	// Map each Google type to your type keys
	const typeToKey: Record<string, keyof fullAddressProps> = {
		administrative_area_level_1: 'administrative_area_level_1',
		administrative_area_level_2: 'administrative_area_level_2',
		country: 'country',
		locality: 'locality',
		sublocality: 'sublocality',
		neighborhood: 'neighborhood',
		postal_code: 'postal_code',
		postal_code_suffix: 'postal_code_suffix',
		route: 'route',
		street_number: 'street_number',
	};

	for (const { long_name, types } of addressComponents) {
		for (const type of types) {
			const key = typeToKey[type];
			if (key) {
				result[key] = long_name;
			}
		}
	}

	return result;
};

export function DeliverySearch({
	setCurrentView,
	setSelectedAddress,
	isAddressAvailable,
	deliveryAddress,
	setDeliveryAddress,
}: DeliverySearchProps) {
	const appConfigContext = useContext(ApplicationConfigContext);
	const { createTempBasket, validateDeliveryLocation, isUndeliverable, setIsUndeliverable } =
		useContext(CartContext);
	const [isLoading, setIsLoading] = useState(false);
	const [loadRecentAddress, setLoadRecentAddress] = useState(false);

	const navigateToOrderStart = (selectedAddress: deliveryAddressProps) => {
		setLoadRecentAddress(true);
		// creating a temporary basket to get wanted times
		createTempBasket({
			type: 'delivery',
			deliveryAddress: {
				streetAddress: selectedAddress.lastDeliveryAddress.streetAddress,
				city: selectedAddress.lastDeliveryAddress.city,
				state: selectedAddress.lastDeliveryAddress.state,
				zipCode: selectedAddress.lastDeliveryAddress.zipCode,
				latitude: selectedAddress.latitude ?? 0,
				longitude: selectedAddress.longitude ?? 0,
				specialInstructions: selectedAddress.lastDeliveryAddress.specialInstructions,
			},
			pickupAddress: null,
			orderDateTime: {
				orderDate: new Date(),
				orderTime: 'asap',
			},
		})
			.then(() => {
				setLoadRecentAddress(false);
				setDeliveryAddress(selectedAddress);
				setCurrentView('selection');
			})
			.catch((err) => {
				console.log('Error: ', err);
				setLoadRecentAddress(false);
			});
	};

	const navigateToDeliveryForm = (selectedValue: PlaceSuggestion) => {
		setIsLoading(true);

		return geocodeMinimal(selectedValue.place_id)
			.then(apiCallForValidateAddress)
			.then((isSuccess: boolean) => {
				if (isSuccess) {
					setCurrentView('deliveryForm');
				}
			})
			.catch((err) => {
				console.error('Geocoding failed:', err);
				setIsUndeliverable(true);
			})
			.finally(() => {
				setIsLoading(false);
			});
	};

	const geocodeMinimal = (placeId: string): Promise<MinimalGeocodeResult[]> => {
		const geocoder = new google.maps.Geocoder();
		return new Promise((resolve, reject) => {
			geocoder.geocode({ placeId }, (results, status) => {
				if (status === google.maps.GeocoderStatus.OK && results) {
					const minimal = results.map((r) => ({
						geometry: r.geometry,
						address_components: r.address_components,
						types: r.types,
					}));
					resolve(minimal);
				} else {
					reject(status);
				}
			});
		});
	};

	const apiCallForValidateAddress = async (results: MinimalGeocodeResult[]) => {
		// If it's a route type, mark undeliverable
		if (results[0].types.includes('route')) {
			setIsUndeliverable(true);
			setIsLoading(false);
			return false;
		}

		// Extract address (now Partial<fullAddressProps>)
		const fullAddress = extractAddressFromComponents(results[0].address_components);

		// Safely build fields with nullish coalescing
		const streetAddress = `${fullAddress.street_number ?? ''} ${fullAddress.route ?? ''}`.trim();
		const city = fullAddress.locality ?? fullAddress.sublocality ?? fullAddress.neighborhood ?? '';
		const state = fullAddress.administrative_area_level_1 ?? '';
		const zipCode = fullAddress.postal_code ?? '';
		const latitude = results[0].geometry.location.lat();
		const longitude = results[0].geometry.location.lng();

		//check delivery address availability
		const isAddressAvailable = await validateDeliveryLocation({
			city: city,
			streetAddress: streetAddress,
			zipCode: zipCode,
			id: '',
			building: '',
			state: state,
			phoneNumber: '',
			isDefault: false,
			latitude: latitude,
			longitude: longitude,
			specialInstructions: '',
			addressType: 'delivery',
		});

		if (isAddressAvailable) {
			setSelectedAddress(results);
		}
		return isAddressAvailable;
	};

	const errorMessage =
		'Delivery is not available to that address. Please update your address or change to Pickup.';

	return (
		<div className={styles.shopSearch}>
			<div className="fixed">
				<button aria-label="back" onClick={() => setCurrentView('selection')}>
					<BackArrow className={styles.backArrow} onClick={() => setIsUndeliverable(false)} />
				</button>
			</div>
			<h3 className={`${styles.title} order-title text-center`} data-type-style="subtitle-2">
				Delivery Location
			</h3>
			{isUndeliverable && !isLoading && (
				<p className="mb-4 text-red-600" data-type-style="metadata-1">
					{errorMessage}
				</p>
			)}
			{appConfigContext && (
				<GooglePlacesAutocomplete
					apiKey={`${appConfigContext.GOOGLE_MAPS_TOKEN}`}
					selectProps={{
						components: {
							LoadingIndicator: () => <Loading size="small" fill="gray" />,
						},
						onChange: (option) => {
							if (option) {
								const suggestion: PlaceSuggestion = {
									place_id: option.value.place_id,
									description: option.value.description,
								};
								navigateToDeliveryForm(suggestion);
							} else {
								setIsUndeliverable(false);
							}
						},
						onFocus: () => {
							if (typeof window !== 'undefined') {
								window.dataLayer = window.dataLayer || [];
								const eventData = {
									event: 'journey_element',
									eventModel: {
										type: 'input',
										action: 'focus',
										location: 'delivery search',
										text: 'Enter your address',
										pathname: window.location.pathname,
										domain: window.location.hostname,
									},
								};
								window.dataLayer.push(eventData);
							}
						},
						placeholder: 'Enter your address',
						isClearable: true,
						className: styles.googlePlacesAutocomplete,
						styles: {
							control: (baseStyles) => ({
								...baseStyles,
								border: 'none',
								borderRadius: 'var(--Spacing-sm, 8px)',
								height: '50px',
								boxShadow: 'none',
								display: 'flex',
								alignItems: 'center',
							}),
							input: (baseStyles) => ({
								...baseStyles,
								...searchIcon(isLoading),
								fontSize: '16px',
								paddingLeft: '35px', // Add space for the search icon
								paddingRight: '40px',
								lineHeight: '1.25rem',
								// textOverflow: 'ellipsis',
								whiteSpace: 'nowrap',
								overflow: 'hidden',
							}),
							option: (baseStyles) => ({
								...baseStyles,
								fontSize: '18px',
							}),
							singleValue: (baseStyles) => ({
								...baseStyles,
								fontSize: '18px',
								paddingLeft: '35px', // Align with input padding
								display: 'flex',
								alignItems: 'center',
							}),
							indicatorsContainer: (baseStyles) => ({
								...baseStyles,
								svg: {
									marginRight: '5px',
								},
							}),
							dropdownIndicator: (baseStyles) => ({
								...baseStyles,
								display: 'none',
							}),
							placeholder: (baseStyles) => ({
								...baseStyles,
								fontSize: '15px',
								paddingLeft: '35px', // Align placeholder with input padding
							}),
							valueContainer: (baseStyles) => ({
								...baseStyles,
								paddingLeft: '5px',
							}),
						},
					}}
					autocompletionRequest={{
						componentRestrictions: {
							country: ['us'],
						},
						types: ['address'],
					}}
					onLoadFailed={(error) => {
						console.log(error);
					}}
				/>
			)}
			{isLoading ? (
				<div className="flex justify-center">
					<Loading size="large" fill="green" />
				</div>
			) : (
				<></>
			)}
			{isAddressAvailable && (
				<div className="my-8 grid grid-rows-1 gap-2">
					<p data-ink="light" data-type-style="label" className="font-black">
						RECENT SEARCHES
					</p>

					<StartOrderAddressCard
						title={deliveryAddress?.lastDeliveryAddress?.streetAddress}
						disabled={loadRecentAddress}
						addressChildren={
							<p className="text-sm" data-ink="light" data-type-style="body-3">
								{deliveryAddress?.lastDeliveryAddress?.city},{' '}
								{deliveryAddress?.lastDeliveryAddress?.zipCode}
							</p>
						}
						rightIcon={
							loadRecentAddress ? (
								<Loading size="small" fill="gray" />
							) : (
								<Arrow className="ms-auto" />
							)
						}
						ctaAction={() => {
							if (deliveryAddress) {
								// Track the address selection event
								if (typeof window !== 'undefined') {
									window.dataLayer = window.dataLayer || [];
									const eventData = {
										event: 'journey_element',
										eventModel: {
											type: 'address',
											action: 'select',
											location: 'delivery search',
											text: 'delivery address',
											pathname: window.location.pathname,
											domain: window.location.hostname,
										},
									};
									window.dataLayer.push(eventData);
								}
								navigateToOrderStart(deliveryAddress);
							}
						}}
					/>
				</div>
			)}
		</div>
	);
}
